import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  Grid,
  FormHelperText,
  Autocomplete,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Send, 
  Preview, 
  Email, 
  Close, 
  CheckCircle, 
  Error,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon
} from '@mui/icons-material';

const AdminEmailTool = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [emailData, setEmailData] = useState({
    subject: '',
    heading: '',
    bodyContent: '',
    emailType: 'marketing',
    ctaText: '',
    ctaUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [previewHtml, setPreviewHtml] = useState('');
  const [tenantProfile, setTenantProfile] = useState(null);
  const [bodyContentRef, setBodyContentRef] = useState(null);

  const emailTypes = [
    { value: 'marketing', label: 'Marketing', icon: '📢' },
    { value: 'announcement', label: 'Announcement', icon: '📣' },
    { value: 'promotion', label: 'Promotion', icon: '🎉' },
    { value: 'newsletter', label: 'Newsletter', icon: '📰' },
    { value: 'update', label: 'Update', icon: '✨' }
  ];

  const subjectSuggestions = {
    marketing: [
      'Discover Our New Cleaning Services',
      'Special Offer for Valued Customers',
      'Your Home Deserves the Best Care'
    ],
    announcement: [
      'Important Update from PrimeShine',
      'New Features Available Now',
      'Service Area Expansion Announcement'
    ],
    promotion: [
      '🎉 Limited Time Offer - Save 20%',
      'Flash Sale: Premium Cleaning Services',
      'Exclusive Deal Just for You'
    ],
    newsletter: [
      'PrimeShine Monthly Newsletter',
      'Cleaning Tips & Company Updates',
      'What\'s New This Month'
    ],
    update: [
      'Account Update Required',
      'New Booking Options Available',
      'Service Schedule Changes'
    ]
  };

  // Fetch clients and tenant profile when component mounts
  useEffect(() => {
    if (open) {
      fetchClients();
      fetchTenantProfile();
    }
  }, [open]);

  // Generate preview when email data changes
  useEffect(() => {
    if (emailData.subject && emailData.heading && emailData.bodyContent && tenantProfile) {
      generatePreview();
    }
  }, [emailData, selectedClients, tenantProfile]);

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/clients`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clients');
      }

      const data = await response.json();
      setClients(data);
    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: `Failed to load clients: ${error.message}`
      });
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchTenantProfile = async () => {
    try {
      // For now, we'll create a mock tenant profile
      // In a real implementation, you'd fetch this from an API
      const mockTenant = {
        companyName: 'PrimeShine',
        name: 'PrimeShine',
        email: 'contact@primeshine.com',
        contactEmail: 'contact@primeshine.com',
        phone: '(123) 456-7890',
        contactPhone: '(123) 456-7890',
        website: 'https://www.primeshine.com',
        logoUrl: null
      };
      setTenantProfile(mockTenant);
    } catch (error) {
      console.error('Error fetching tenant profile:', error);
      // Set a fallback tenant profile
      setTenantProfile({
        companyName: 'PrimeShine',
        name: 'PrimeShine',
        email: 'contact@company.com',
        contactEmail: 'contact@company.com',
        phone: '(123) 456-7890',
        contactPhone: '(123) 456-7890'
      });
    }
  };

  const formatTextForHtml = (text) => {
    // Convert markdown-like formatting to HTML
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
      .replace(/_(.*?)_/g, '<u>$1</u>') // Underline
      .replace(/\n/g, '<br>') // Line breaks
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #1976d2; text-decoration: none;">$1</a>'); // Links
  };

  const insertFormatting = (format) => {
    if (!bodyContentRef) return;
    
    const textarea = bodyContentRef;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    
    let newText = '';
    let cursorOffset = 0;
    
    switch (format) {
      case 'bold':
        newText = `**${selectedText || 'bold text'}**`;
        cursorOffset = selectedText ? 0 : -2;
        break;
      case 'italic':
        newText = `*${selectedText || 'italic text'}*`;
        cursorOffset = selectedText ? 0 : -1;
        break;
      case 'underline':
        newText = `_${selectedText || 'underlined text'}_`;
        cursorOffset = selectedText ? 0 : -1;
        break;
      case 'link':
        newText = `[${selectedText || 'link text'}](https://example.com)`;
        cursorOffset = selectedText ? -21 : -34;
        break;
      default:
        return;
    }
    
    const fullText = beforeText + newText + afterText;
    handleInputChange('bodyContent', fullText);
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + newText.length + cursorOffset;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const generatePreview = () => {
    const selectedClient = selectedClients[0];
    const recipientName = selectedClient ? selectedClient.firstName : 'John';
    
    const typeIcons = {
      'marketing': '📢',
      'announcement': '📣',
      'promotion': '🎉',
      'newsletter': '📰',
      'update': '✨'
    };

    const typeIcon = typeIcons[emailData.emailType] || '📧';
    const companyName = tenantProfile?.companyName || tenantProfile?.name || 'PrimeShine';
    const contactEmail = tenantProfile?.contactEmail || tenantProfile?.email || 'contact@primeshine.com';
    const contactPhone = tenantProfile?.contactPhone || tenantProfile?.phone || '(123) 456-7890';
    const website = tenantProfile?.website || '';
    
    const formattedBodyContent = formatTextForHtml(emailData.bodyContent);
    
    const preview = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 600px; margin: 0 auto; background-color: #f5f5f5; padding: 20px;">
        <div style="background-color: #ffffff; border-radius: 10px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <div style="background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); color: white; text-align: center; padding: 30px 20px;">
            ${tenantProfile?.logoUrl ? `<img src="${tenantProfile.logoUrl}" alt="${companyName}" style="max-width: 150px; height: auto; margin-bottom: 10px;">` : ''}
            <h1 style="font-size: 24px; font-weight: bold; margin: 10px 0 5px 0;">${companyName}</h1>
            <p style="font-size: 16px; opacity: 0.9; margin: 0;">${typeIcon} ${emailData.emailType.charAt(0).toUpperCase() + emailData.emailType.slice(1)}</p>
          </div>
          
          <div style="padding: 40px 30px;">
            <div style="font-size: 20px; font-weight: bold; color: #1976d2; margin-bottom: 20px;">Hi ${recipientName}!</div>
            
            <div style="font-size: 28px; font-weight: bold; color: #1976d2; text-align: center; margin: 30px 0; line-height: 1.3;">${emailData.heading}</div>
            
            <div style="font-size: 16px; margin-bottom: 30px; line-height: 1.7;">${formattedBodyContent}</div>
            
            ${emailData.ctaText && emailData.ctaUrl ? `
            <div style="text-align: center; margin: 35px 0;">
              <a href="${emailData.ctaUrl}" style="display: inline-block; background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); color: white; text-decoration: none; padding: 15px 40px; border-radius: 50px; font-size: 18px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px;">${emailData.ctaText}</a>
            </div>
            ` : ''}
            
            <div style="text-align: center; margin: 30px 0; padding: 20px; background-color: #f8f9fa; border-radius: 8px;">
              <strong>Questions or need assistance?</strong><br>
              We're here to help! Contact us at:<br>
              📧 <a href="mailto:${contactEmail}">${contactEmail}</a><br>
              📞 <a href="tel:${contactPhone}">${contactPhone}</a>
            </div>
            
            <div style="text-align: center; font-style: italic; color: #666; margin-top: 30px;">
              Thank you for being a valued part of the ${companyName} community!
            </div>
          </div>
          
          <div style="background-color: #333333; color: white; text-align: center; padding: 25px; font-size: 14px;">
            <p><strong>The ${companyName} Team</strong></p>
            ${website ? `<p><a href="${website}" style="color: #1976d2; text-decoration: none;">${website}</a></p>` : ''}
            <div style="font-size: 12px; color: #999; margin-top: 15px;">
              <p>You received this email because you are a ${companyName} customer.</p>
              <p>If you no longer wish to receive these emails, please <a href="mailto:${contactEmail}?subject=Unsubscribe" style="color: #999; text-decoration: underline;">contact us</a>.</p>
            </div>
          </div>
        </div>
      </div>
    `;
    
    setPreviewHtml(preview);
  };

  const handleInputChange = (field, value) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendEmail = async () => {
    if (!emailData.subject || !emailData.heading || !emailData.bodyContent) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Please fill in all required fields'
      });
      return;
    }

    if (selectedClients.length === 0) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Please select at least one recipient'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/send-marketing-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recipientIds: selectedClients.map(client => client.id),
          subject: emailData.subject,
          heading: emailData.heading,
          bodyContent: emailData.bodyContent,
          emailType: emailData.emailType,
          ctaText: emailData.ctaText || undefined,
          ctaUrl: emailData.ctaUrl || undefined
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      setAlert({
        show: true,
        type: 'success',
        message: `Email sent successfully to ${result.data.totalSent} recipients!`
      });

      // Reset form after successful send
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: `Failed to send email: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmailData({
      subject: '',
      heading: '',
      bodyContent: '',
      emailType: 'marketing',
      ctaText: '',
      ctaUrl: ''
    });
    setSelectedClients([]);
    setActiveTab(0);
    setAlert({ show: false, type: '', message: '' });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Email color="primary" />
        <Typography variant="h6">Email Marketing Tool</Typography>
        <Button
          onClick={handleClose}
          sx={{ ml: 'auto' }}
          startIcon={<Close />}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 2 }}
            onClose={() => setAlert({ show: false, type: '', message: '' })}
            icon={alert.type === 'success' ? <CheckCircle /> : <Error />}
          >
            {alert.message}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label="Compose Email" />
            <Tab label="Preview" disabled={!emailData.subject || !emailData.heading || !emailData.bodyContent} />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: 'fit-content' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Email Content</Typography>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Email Type</InputLabel>
                  <Select
                    value={emailData.emailType}
                    onChange={(e) => handleInputChange('emailType', e.target.value)}
                    label="Email Type"
                  >
                    {emailTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  freeSolo
                  options={subjectSuggestions[emailData.emailType] || []}
                  value={emailData.subject}
                  onChange={(e, value) => handleInputChange('subject', value)}
                  onInputChange={(e, value) => handleInputChange('subject', value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subject Line *"
                      fullWidth
                      sx={{ mb: 2 }}
                      helperText="Choose from suggestions or write your own"
                    />
                  )}
                />

                <TextField
                  label="Main Heading *"
                  value={emailData.heading}
                  onChange={(e) => handleInputChange('heading', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  helperText="Eye-catching title for your email"
                />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Body Content *
                  </Typography>
                  <Paper sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ borderBottom: '1px solid #e0e0e0', p: 1, display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Bold (**text**)">
                        <IconButton size="small" onClick={() => insertFormatting('bold')}>
                          <FormatBold />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Italic (*text*)">
                        <IconButton size="small" onClick={() => insertFormatting('italic')}>
                          <FormatItalic />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Underline (_text_)">
                        <IconButton size="small" onClick={() => insertFormatting('underline')}>
                          <FormatUnderlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Link ([text](url))">
                        <IconButton size="small" onClick={() => insertFormatting('link')}>
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TextField
                      value={emailData.bodyContent}
                      onChange={(e) => handleInputChange('bodyContent', e.target.value)}
                      fullWidth
                      multiline
                      rows={8}
                      variant="outlined"
                      InputProps={{
                        style: { 
                          border: 'none',
                          fontSize: '14px',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                        }
                      }}
                      inputRef={setBodyContentRef}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          '&:hover fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none' }
                        }
                      }}
                    />
                  </Paper>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Use **bold**, *italic*, _underline_, and [link text](url) for formatting
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Call to Action (Optional)</Typography>
                
                <TextField
                  label="Button Text"
                  value={emailData.ctaText}
                  onChange={(e) => handleInputChange('ctaText', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="e.g., Book Now, Learn More, Get Started"
                />

                <TextField
                  label="Button URL"
                  value={emailData.ctaUrl}
                  onChange={(e) => handleInputChange('ctaUrl', e.target.value)}
                  fullWidth
                  placeholder="https://example.com"
                  helperText="Include https:// for external links"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Recipients</Typography>
                
                {clientsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Select Clients *</InputLabel>
                    <Select
                      multiple
                      value={selectedClients}
                      onChange={(e) => setSelectedClients(e.target.value)}
                      input={<OutlinedInput label="Select Clients *" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((client) => (
                            <Chip
                              key={client.id}
                              label={`${client.firstName} ${client.lastName}`}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300
                          }
                        }
                      }}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client}>
                          <Box>
                            <Typography variant="body2">
                              {client.firstName} {client.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.email}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {selectedClients.length} client(s) selected
                    </FormHelperText>
                  </FormControl>
                )}

                {selectedClients.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Selected Recipients:
                    </Typography>
                    {selectedClients.slice(0, 5).map((client) => (
                      <Typography key={client.id} variant="body2" color="text.secondary">
                        • {client.firstName} {client.lastName} ({client.email})
                      </Typography>
                    ))}
                    {selectedClients.length > 5 && (
                      <Typography variant="body2" color="text.secondary">
                        • ... and {selectedClients.length - 5} more
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Email Preview</Typography>
            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          startIcon={<Preview />}
          onClick={() => setActiveTab(1)}
          disabled={!emailData.subject || !emailData.heading || !emailData.bodyContent}
        >
          Preview
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
          onClick={handleSendEmail}
          disabled={loading || !emailData.subject || !emailData.heading || !emailData.bodyContent || selectedClients.length === 0}
        >
          {loading ? 'Sending...' : `Send to ${selectedClients.length} recipient(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminEmailTool;